import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReportOverview } from '../types/analytics-api.types';
import { fmtNum, fmtShortDate } from '../utils/report-format';

const COLOR = { open: '#2a78d6', click: '#eb6834', grid: '#E7E8EA', muted: '#707070' };

type ReportEngagementChartProps = {
  series: ReportOverview['series'];
};

export function ReportEngagementChart({ series }: ReportEngagementChartProps) {
  const data = useMemo(
    () =>
      series.map((point) => ({
        ...point,
        label: fmtShortDate(point.bucket.slice(0, 10)),
      })),
    [series]
  );

  if (!data.length) {
    return <p className="py-16 text-center text-xs text-neutral-500">No events in this range.</p>;
  }

  return (
    <div>
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
            <CartesianGrid stroke={COLOR.grid} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: '#666', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={(v) => fmtNum(Number(v))}
              tick={{ fill: COLOR.muted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={48}
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                fmtNum(value),
                name === 'opens' ? 'Opens' : 'Clicks',
              ]}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ''}
            />
            <Line type="monotone" dataKey="opens" stroke={COLOR.open} strokeWidth={2} dot={false} />
            <Line
              type="monotone"
              dataKey="clicks"
              stroke={COLOR.click}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-center gap-4 text-xs text-neutral-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-sm" style={{ background: COLOR.open }} />
          Opens
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block size-2.5 rounded-sm" style={{ background: COLOR.click }} />
          Clicks
        </span>
      </div>
    </div>
  );
}
